/**
 * PATH: packages/ui/src/kanban/hooks/useFocusTrap.ts
 * FILE_ID: UI-KANBAN-FOCUS-TRAP-001
 * PURPOSE: Full modal focus trap for every kanban role="dialog" surface —
 *   moves focus in on open, cycles Tab/Shift+Tab inside, restores focus on
 *   close, and (opt-in) closes on Escape without leaking to the global
 *   board shortcut chain (useBoardKeyboardShortcuts window listener).
 * WHY: A11y audit 2026-07: 25+ kanban dialogs declared role="dialog" but Tab
 *   walked out into the board behind them and Escape fell through to the
 *   global chain. Calendar's useFocusTrap only wraps Tab (no initial focus /
 *   restore / Escape) — we reuse its getFocusable scanner, not its trap.
 * LANDMINES: document-CAPTURE listener + module-level trap stack — only the
 *   innermost (top) trap acts, and stopPropagation() in capture is what
 *   keeps Escape away from the window-level board shortcuts. Escape from an
 *   editable target blurs instead of closing (same convention as the
 *   CardDetailDrawer esc typing-guard pin).
 */
import { useEffect, useRef, type RefObject } from 'react'

import { getFocusable } from '../../calendar/useFocusTrap'

export interface FocusTrapOptions {
  /** Trap only while true (default true). Safe for conditional dialogs. */
  active?: boolean
  /** Called on Escape (top-most trap only). Omit to keep existing handlers. */
  onEscape?: () => void
}

/** Innermost-dialog bookkeeping: last pushed trap wins Tab/Escape. */
const trapStack: HTMLElement[] = []

function isEditable(el: EventTarget | null): boolean {
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    (el instanceof HTMLElement && el.isContentEditable)
  )
}

function focusInitial(node: HTMLElement): void {
  // Respect a dialog's own autoFocus — only move focus if it's outside.
  if (node.contains(document.activeElement)) return
  const first = getFocusable(node)[0]
  if (first) {
    first.focus()
    return
  }
  if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '-1')
  node.focus()
}

function cycleTab(node: HTMLElement, e: KeyboardEvent): void {
  const list = getFocusable(node)
  if (list.length === 0) {
    e.preventDefault()
    node.focus()
    return
  }
  const first = list[0]
  const last = list[list.length - 1]
  const current = document.activeElement as HTMLElement | null
  if (!current || !node.contains(current)) {
    // Focus escaped (e.g. backdrop click landed on body) — pull it back in.
    e.preventDefault()
    ;(e.shiftKey ? last : first).focus()
    return
  }
  if (e.shiftKey) {
    if (current === first || current === node) {
      e.preventDefault()
      last.focus()
    }
  } else if (current === last || current === node) {
    e.preventDefault()
    first.focus()
  }
}

/**
 * Trap keyboard focus within `ref` while `active`.
 * Mount: saves document.activeElement, moves focus to the first focusable
 * element (or the container with tabindex=-1). Unmount: restores focus.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  { active = true, onEscape }: FocusTrapOptions = {},
): void {
  const onEscapeRef = useRef(onEscape)
  onEscapeRef.current = onEscape

  useEffect(() => {
    if (!active) return undefined
    const node = ref.current
    if (!node) return undefined

    const previouslyFocused = document.activeElement as HTMLElement | null
    trapStack.push(node)
    focusInitial(node)

    function onKeyDown(e: KeyboardEvent) {
      if (trapStack[trapStack.length - 1] !== node) return
      if (e.key === 'Tab') {
        cycleTab(node, e)
        return
      }
      if (e.key === 'Escape' && onEscapeRef.current) {
        // Never let a dialog Escape reach the global board shortcut chain.
        e.preventDefault()
        e.stopPropagation()
        if (isEditable(e.target)) {
          // Typing-guard: first Esc leaves the field, second closes.
          ;(e.target as HTMLElement).blur()
          return
        }
        onEscapeRef.current()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      const idx = trapStack.indexOf(node)
      if (idx >= 0) trapStack.splice(idx, 1)
      if (previouslyFocused?.isConnected) previouslyFocused.focus()
    }
  }, [ref, active])
}
